<script>
    import Select from "svelte-select/Select.svelte";

    import { onMount } from "svelte";

    import { user } from "../../stores";

    import {
        DirectUserRoles,
        userFilterTypes,
    } from "../../Utils/utilEnums.svelte";

    import {
        goBack,
        showError,
        getTitle,
        goToURL,
    } from "../../Utils/utilFunctions.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        Button,
        CardLayout,
        NotificationModal,
        PageHead,
        PageLoading,
    } from "../../Components/Common/index.svelte";
    import { cacheDefaultFilters } from "../../Utils/utilCache.svelte";
    import { searchGroupsFromCache } from "../../Utils/DataLoaders.svelte";

    $: isUserAdmin = $user.role == DirectUserRoles.Admin;

    $: isUserManager = $user.role == DirectUserRoles.Manager;

    $: isSubmitBtnDisabled =
        !userFilter.name || !returnedDoctors.some((doctor) => doctor.checked);

    let isLoadingDoctors = false,
        returnedDoctors = [];

    let isCreatingFilter = false,
        userFilter = {};

    let selectedGroup = {};

    let showSuccessModal = false;

    onMount(async () => {
        if (!isUserAdmin && !isUserManager) goBack();
        else await loadDoctors();
    });

    const loadDoctors = async () => {
        isLoadingDoctors = true;

        let searchParams = isUserManager
            ? {
                  "filter[groupID]": $user.groupId,
              }
            : selectedGroup?.group?.id
              ? {
                    "filter[groupID]": selectedGroup.group.id,
                }
              : {};

        await apiMethods.getMethod
            .getAccount(0, 999999, searchParams)
            .then((res) => _handleLoadDoctors(res));

        isLoadingDoctors = false;
    };

    const clearSelectedGroup = async () => {
        selectedGroup = {};
        await loadDoctors();
    };

    const handleSelectGroup = async () => await loadDoctors();

    const createFilter = async () => {
        isCreatingFilter = true;

        _syncFilterData();

        await apiMethods.postMethod
            .createDefaultFilter(userFilter)
            .then((res) => _handleCreateFilter(res));

        isCreatingFilter = false;
    };

    const toggleSelectDoctor = (doctorId) => {
        const targetDoctor = returnedDoctors.find(
            (doctor) => doctor.id == doctorId,
        );

        if (!targetDoctor) return;

        returnedDoctors = returnedDoctors.map((doctor) =>
            doctor.id === doctorId
                ? { ...doctor, checked: targetDoctor.checked }
                : doctor,
        );
    };

    const afterCreateSuccess = () => {
        _closeSuccessModal();
        goToURL(`/DefaultFilters`);
    };

    // HELPER FUNCTIONS
    const _handleLoadDoctors = (res) => {
        if (!res || res.error)
            showError(
                res.error ??
                    "Failed to load doctors, please refresh the page and try again and contact support if the issue persists.",
            );
        else {
            const prevReturnDoctors = [...returnedDoctors];
            returnedDoctors = [];

            res.results.map((doctor) =>
                returnedDoctors.push({
                    id: doctor.id,
                    name: `${getTitle(doctor.title)} ${doctor.fullName}`,
                    checked: prevReturnDoctors.length
                        ? prevReturnDoctors.find((d) => d.id == doctor.id)
                              ?.checked
                        : false,
                }),
            );
        }
    };

    const _handleCreateFilter = async (res) => {
        if (!res || res.error)
            showError(
                res.error ??
                    "Failed to create filter, please refresh the page and try again and contact support if the issue persists.",
            );
        else {
            await cacheDefaultFilters();

            userFilter.id = res.id;

            _openSuccessModal();
        }
    };

    const _syncFilterData = () => {
        if (isUserManager) userFilter.groupId = $user.groupId;
        else if (selectedGroup?.group)
            userFilter.groupId = selectedGroup.group.id;
        else delete userFilter.groupId;

        const selectedDoctorIds = [];

        returnedDoctors.map((doctor) => {
            if (doctor.checked) selectedDoctorIds.push(doctor.id);
        });

        userFilter.filter = selectedDoctorIds.join(",");
    };

    const _openSuccessModal = () => (showSuccessModal = true);

    const _closeSuccessModal = () => (showSuccessModal = false);
</script>

<PageHead title="Create New Filter" />

<div class="relative flex-1">
    <div class="absolute inset-0 overflow-y-auto">
        <CardLayout title="Filters Details">
            <div
                class="flex flex-col px-2 py-4 w-full sticky top-0 z-[1] bg-white lg:flex-row"
            >
                <div class="flex flex-col flex-1 gap-y-4 h-auto">
                    <div class="flex gap-x-4 items-center align-center">
                        <label for="name" class="w-1/3 text-right">
                            Name
                        </label>

                        <input
                            type="text"
                            required
                            id="name"
                            name="name"
                            class="input input-bordered input-sm flex flex-1"
                            bind:value={userFilter.name}
                        />
                    </div>

                    <div class="flex gap-x-4 items-center align-center">
                        <label for="type" class="w-1/3 text-right">
                            Type
                        </label>

                        <select
                            id="type"
                            name="type"
                            bind:value={userFilter.filterType}
                            class="input input-bordered input-sm flex flex-1"
                        >
                            {#each userFilterTypes as { id, type }}
                                <option value={id} selected={id == 0}
                                    >{type}</option
                                >
                            {/each}
                        </select>
                    </div>

                    {#if isUserAdmin}
                        <div class="flex gap-x-4 items-center align-center">
                            <label for="type" class="w-1/3 text-right">
                                Group
                            </label>

                            <div class="flex items-center flex-1">
                                <Select
                                    bind:value={selectedGroup.group}
                                    class="flex flex-1 max-h-[35px] rounded-full"
                                    loadOptions={searchGroupsFromCache}
                                    itemId="id"
                                    label="name"
                                    on:clear={clearSelectedGroup}
                                    on:change={handleSelectGroup}
                                    noOptionsMessage="No Groups Found"
                                />
                            </div>
                        </div>
                    {/if}
                </div>

                <div class="divider lg:divider-horizontal" />

                <div
                    class="flex flex-1 items-end justify-end px-4 gap-4 h-auto"
                >
                    <Button clickFunc={goBack} manualClass="w-[64px] btn-error">
                        Cancel
                    </Button>

                    <Button
                        clickFunc={createFilter}
                        manualClass={isSubmitBtnDisabled
                            ? "w-[64px] text-primary"
                            : "w-[64px]"}
                        isDisabled={isSubmitBtnDisabled}
                    >
                        Create
                    </Button>
                </div>
            </div>

            <div class="divider px-4">CHOOSE AUTO-SEARCH DOCTORS</div>

            {#if isLoadingDoctors}
                <PageLoading />
            {:else}
                <div class="flex flex-1 px-4">
                    <div class="grid grid-cols-3 flex-1 gap-4 mb-6">
                        {#each returnedDoctors as { id, name, checked }}
                            <div class="flex flex-1 gap-x-2 items-center pl-8">
                                <input
                                    type="checkbox"
                                    id={`doctor-${id}`}
                                    class="checkbox checkbox-sm"
                                    bind:checked
                                    on:change={() => toggleSelectDoctor(id)}
                                />

                                <label
                                    for={`doctor-${id}`}
                                    class="cursor-pointer"
                                >
                                    {name}
                                </label>
                            </div>
                        {/each}
                    </div>
                </div>
            {/if}
        </CardLayout>
    </div>
</div>

<NotificationModal
    isModalOpen={showSuccessModal}
    title="Success"
    closeModal={afterCreateSuccess}
    showConfirmBtn={false}
    closeModalFunction={afterCreateSuccess}
>
    Filter created successfully
</NotificationModal>
